import type { ReactNode } from 'react';
import dayjs from 'dayjs';
import {
	Redirect,
	Link as RouterLink,
	useLocation,
	useParams,
} from 'react-router-dom';

import Tab from '@liquidweb-dev/undercurrent/Tab';
import Tabs from '@liquidweb-dev/undercurrent/Tabs';
import CancellationAlert from 'components/CancellationAlert';
import Attributes from 'components/HostingDetailsAttributes';
import Description from 'components/HostingDetailsDescription';
import FullScreenError from 'components/legacy/FullScreenError';
import PageLayout, { PageLayoutInsetContent } from 'components/PageLayout';
import TabBar from 'components/TabBar';
import useAssetDetails from 'modules/queries/asset/useDetails';
import NotFound from 'pages/NotFound';
import hostingDetailsAlsowith from 'pages/servers/details/hostingDetailsAlsowith';
import useDocumentTitle from 'utility/hooks/useDocumentTitle';
import useTriggerServerViewEvent from 'utility/hooks/useTriggerServerViewEvent';
import { getErrorStringHelper } from 'utility/redux/selectorHelperFunctions/apiModules';
import Actions from './Actions';

export interface NutanixLayoutProps {
	children: ReactNode;
}

/**
 * A layout for Nutanix Private Cloud servers' details page.
 */
const NutanixLayout = ({ children }: NutanixLayoutProps) => {
	const { uniqId } = useParams<{ uniqId: string }>();
	const { pathname } = useLocation();

	const {
		data: assetDetailsData,
		isLoading: assetDetailsIsLoading,
		isError: assetDetailsIsError,
		error: assetDetailsError,
	} = useAssetDetails({
		uniq_id: uniqId,
		alsowith: hostingDetailsAlsowith,
	});

	const nutanixDocumentTitle =
		assetDetailsData?.custom_name || assetDetailsData?.domain || '';
	useDocumentTitle(nutanixDocumentTitle || 'Server Details');
	useTriggerServerViewEvent({ uniqId });

	if (assetDetailsIsError && assetDetailsError.statusCode === 404) {
		return <NotFound />;
	}

	if (assetDetailsIsError) {
		return (
			<FullScreenError content={getErrorStringHelper(assetDetailsError)} />
		);
	}

	const {
		custom_name: customName,
		custom_description: customDescription,
		domain,
		isNutanixPrivateCloud,
	} = assetDetailsData || {};

	// redirect assets that are not a Nutanix private cloud, VMs included
	if (isNutanixPrivateCloud === false) {
		return <Redirect to={`/servers/${uniqId}`} />;
	}

	// the tabs correspond to the last portion of the path after 'nutanix'
	const tabsValue = pathname.split('/')[4] || 'overview';

	const title = customName || domain || '';
	// prevent extra gap in PageLayout
	const shouldRenderDescription =
		Boolean(customDescription) || !(title === domain);

	const cancellationDate =
		assetDetailsData?.scheduledCancellationDate?.DateTime;
	const isCancellationScheduled = cancellationDate
		? dayjs(cancellationDate).isSameOrAfter(dayjs(), 'day')
		: false;

	const nextRenewalDate = dayjs(
		assetDetailsData?.nextInvoiceDate?.DateTime,
	).format('MMMM D, YYYY');

	return (
		<>
			{isCancellationScheduled && (
				<CancellationAlert
					cancellationDate={cancellationDate}
					uniqId={uniqId}
					title={title}
					nextRenewalDate={nextRenewalDate}
				/>
			)}
			<PageLayout
				title={title}
				attributesSlot={<Attributes />}
				actionsSlot={<Actions />}
				isLoading={assetDetailsIsLoading}
				description={
					shouldRenderDescription && (
						<Description serverDescription={customDescription} />
					)
				}
				variant="dynamic"
			>
				<TabBar sx={{ marginBottom: 600 }}>
					<Tabs value={tabsValue} variant="scrollable">
						<Tab
							component={RouterLink}
							value="overview"
							to={`/servers/${uniqId}/nutanix`}
							label="Overview"
						/>
					</Tabs>
				</TabBar>
				<PageLayoutInsetContent sx={{ flexGrow: 1 }}>
					{children}
				</PageLayoutInsetContent>
			</PageLayout>
		</>
	);
};

export default NutanixLayout;
